// src/pages/MarketplaceManage.jsx
import React, { useEffect, useState } from "react";
import {
  listenMarketplaceDrafts,
  listenMarketplaceItemsByStatus,
  deleteMarketplaceDraft,
  deleteMarketplaceItem,
} from "../services/data";

export default function MarketplaceManage({
  user,
  onBack,
  onCreate,
  onEditDraft,
  canModerate,
}) {
  const [tab, setTab] = useState("drafts");
  const [drafts, setDrafts] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [publishedItems, setPublishedItems] = useState([]);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  // --- Chargement des données ---
  useEffect(() => {
    if (!user?.uid) return;

    const unsubs = [];

    // Brouillons
    unsubs.push(listenMarketplaceDrafts(user.uid, setDrafts));

    // En attente de validation
    unsubs.push(
      listenMarketplaceItemsByStatus(
        { status: "pending_review", ownerUid: user.uid },
        setPendingItems
      )
    );

    // Publiées
    unsubs.push(
      listenMarketplaceItemsByStatus(
        { status: "published", ownerUid: user.uid },
        setPublishedItems
      )
    );

    // Refusées
    unsubs.push(
      listenMarketplaceItemsByStatus(
        { status: "rejected", ownerUid: user.uid },
        setRejectedItems
      )
    );

    return () => {
      unsubs.forEach((u) => u && u());
    };
  }, [user?.uid]);

  // --- Actions : suppression brouillon ---
  const handleDeleteDraft = async (draft) => {
    if (!user?.uid || !draft?.id) return;

    const ok = window.confirm(
      "Supprimer ce brouillon ? Cette action est définitive."
    );
    if (!ok) return;

    setBusyId(`draft:${draft.id}`);
    try {
      await deleteMarketplaceDraft(user.uid, draft.id);
      alert("Brouillon supprimé.");
    } catch (err) {
      console.error(err);
      alert("Erreur pendant la suppression du brouillon.");
    } finally {
      setBusyId(null);
    }
  };

  // --- Actions : suppression publication (utilisé ici pour les refusées) ---
  const handleDeleteItem = async (item) => {
    if (!item?.id) return;

    const ok = window.confirm(
      "Supprimer cette publication ? Cette action est définitive."
    );
    if (!ok) return;

    setBusyId(`item:${item.id}`);
    try {
      await deleteMarketplaceItem(item.id);
      alert("Publication supprimée.");
    } catch (err) {
      console.error(err);
      alert("Erreur pendant la suppression de la publication.");
    } finally {
      setBusyId(null);
    }
  };

  // --- Aperçu simple (modal light / alert pour l’instant) ---
  const handlePreviewItem = (item) => {
    const lignes = [
      item.title || "(Sans titre)",
      item.subtitle || "",
      "",
      item.description || "",
      "",
      item.isPremium
        ? `Prix : ${item.price || 0} ${item.currency || "MAD"}`
        : "Gratuit",
    ];
    alert(lignes.filter(Boolean).join("\n"));
  };

  // --- Rendu des lignes selon le type ---
  const renderRows = (rows, type) => {
    if (!rows || rows.length === 0) {
      return (
        <tr>
          <td colSpan={6} style={{ textAlign: "center", padding: "16px 0" }}>
            Aucun élément pour ce statut.
          </td>
        </tr>
      );
    }

    return rows.map((it) => {
      const updated =
        it.updatedAt && typeof it.updatedAt.toDate === "function"
          ? it.updatedAt.toDate()
          : it.updatedAt instanceof Date
          ? it.updatedAt
          : null;

      return (
        <tr key={it.id}>
          <td>{it.title || "(Sans titre)"}</td>
          <td>{it.category || "-"}</td>
          <td>{it.sourceModeleTitle || it.sourceModeleId || "-"}</td>
          <td>{updated ? updated.toLocaleString() : "-"}</td>
          <td>
            <span className={`badge badge-status badge-${type}`}>
              {type === "drafts"
                ? "Brouillon"
                : type === "pending"
                ? "En attente"
                : type === "published"
                ? "Publiée"
                : "Refusée"}
            </span>
          </td>
          <td className="actions">
            <div className="actions">
              {type === "drafts" && (
                <>
                  {/* Éditer brouillon → MarketplacePublish avec draft pré-rempli */}
                  <button
                    type="button"
                    className="action-btn"
                    title="Éditer le brouillon"
                    onClick={() => onEditDraft && onEditDraft(it)}
                    disabled={busyId === `draft:${it.id}`}
                  >
                    <i className="fas fa-pen" />
                  </button>

                  {/* Supprimer brouillon */}
                  <button
                    type="button"
                    className="action-btn danger"
                    title="Supprimer le brouillon"
                    onClick={() => handleDeleteDraft(it)}
                    disabled={busyId === `draft:${it.id}`}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </>
              )}

              {type !== "drafts" && (
                <>
                  {/* Éditer la fiche (pending / published / rejected) → ouvre MarketplacePublish */}
                  <button
                    type="button"
                    className="action-btn"
                    title="Éditer la fiche"
                    onClick={() => onEditDraft && onEditDraft(it)}
                    disabled={busyId === `item:${it.id}`}
                  >
                    <i className="fas fa-pen" />
                  </button>

                  {/* Supprimer la publication (quel que soit le statut) */}
                  <button
                    type="button"
                    className="action-btn danger"
                    title="Supprimer la publication"
                    onClick={() => handleDeleteItem(it)}
                    disabled={busyId === `item:${it.id}`}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  // --- Sélection des lignes en fonction de l’onglet courant ---
  let currentRows = drafts;
  let currentType = "drafts";

  if (tab === "pending") {
    currentRows = pendingItems;
    currentType = "pending";
  } else if (tab === "published") {
    currentRows = publishedItems;
    currentType = "published";
  } else if (tab === "rejected") {
    currentRows = rejectedItems;
    currentType = "rejected";
  }

  return (
    <div className="page marketplace-manage">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-store" />
          <span>Gestion des publications</span>
        </div>
        <div className="page-actions">
          {onBack && (
            <button type="button" className="btn btn-outline" onClick={onBack}>
              <i className="fas fa-arrow-left" /> Retour
            </button>
          )}
          {onCreate && (
            <button type="button" className="btn btn-primary" onClick={onCreate}>
              <i className="fas fa-plus" /> Nouvelle publication
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="tabs-navigation">
        <div className="tabs-container">
          <button
            type="button"
            className={`tab ${tab === "drafts" ? "active" : ""}`}
            onClick={() => setTab("drafts")}
          >
            Brouillons ({drafts.length})
          </button>
          <button
            type="button"
            className={`tab ${tab === "pending" ? "active" : ""}`}
            onClick={() => setTab("pending")}
          >
            En attente ({pendingItems.length})
          </button>
          <button
            type="button"
            className={`tab ${tab === "published" ? "active" : ""}`}
            onClick={() => setTab("published")}
          >
            Publiées ({publishedItems.length})
          </button>
          <button
            type="button"
            className={`tab ${tab === "rejected" ? "active" : ""}`}
            onClick={() => setTab("rejected")}
          >
            Refusées ({rejectedItems.length})
          </button>
        </div>
      </div>

      {/* Tableau principal */}
      <div className="config-section">
        <div className="config-card">
          <div className="config-body">
            <div className="table-container">
              <table className="placeholders-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Modèle source</th>
                    <th>Mis à jour</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>{renderRows(currentRows, currentType)}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
